import { Download, LayoutTemplate, Plus, Upload } from 'lucide-react';
import { authStoreUserTournamentId } from 'modules/auth/store/selectors';
import { MediaManagerTemplate } from 'modules/core/domain/interfaces/supabase/media-manager/entity';
import { useMediaManagerTemplatesQuery } from 'modules/manager/store/media-manager-api';
import ImportConflictsModal from 'modules/manager/ui/components/import-conflicts-modal';
import ImportTemplatesResultModal from 'modules/manager/ui/components/import-templates-result-modal';
import MediaManagerTemplateList from 'modules/manager/ui/components/media-manager-template-list';
import { useExportTemplates } from 'modules/manager/ui/hooks/use-export-templates';
import { useImportTemplates } from 'modules/manager/ui/hooks/use-import-templates';
import { Pages } from 'modules/navigation/domain/enums/pages';
import { organizerInfoSelector } from 'modules/organizer/store/organizer/selectors';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { deriveAsyncState } from 'shared/utils/async-state';
import UDButton from 'ud-ui/components/button';
import UDHeaderTitle from 'ud-ui/components/header-title';
import { useMainLayout } from 'modules/app-shell/ui/layout/context';
import useCreateTemplateModal from '../hooks/modals/use-create-template-modal';
import useDeleteTemplateModal from '../hooks/modals/use-delete-template-modal';
import useEditTemplateModal from '../hooks/modals/use-edit-template-modal';

/** How long a freshly duplicated template stays highlighted in the list. */
const HIGHLIGHT_DURATION_MS = 4000;

/** Router state accepted by this page — see the duplicate flow on the presets page. */
interface TemplatesListLocationState {
  highlightTemplateId?: number;
}

const MediaManagerTemplatesListPage: React.FC = () => {
  const { setHeaderContent } = useMainLayout();
  const navigate = useNavigate();
  const location = useLocation();
  const tournamentId = useSelector(authStoreUserTournamentId);

  const {
    data: templates = [],
    currentData,
    isFetching,
    isError,
    refetch,
  } = useMediaManagerTemplatesQuery({ tournamentId: tournamentId ?? 0 }, { skip: !tournamentId });
  // Под `skip` (организация ещё не определена) это `loading`, а не «шаблонов нет».
  const listState = deriveAsyncState({
    itemsCount: templates.length,
    hasCurrentData: currentData !== undefined,
    isFetching,
    isError,
  });
  const [highlightedId, setHighlightedId] = useState<number | null>(null);

  const requestedHighlightId = (location.state as TemplatesListLocationState | null)?.highlightTemplateId ?? null;

  useEffect(() => {
    if (requestedHighlightId == null) return;
    setHighlightedId(requestedHighlightId);
    // Consume the router state so a reload or a back-navigation does not flash the row again.
    navigate(Pages.MEDIA_MANAGER.INDEX, { replace: true, state: null });
  }, [requestedHighlightId, navigate]);

  useEffect(() => {
    if (highlightedId == null) return;
    const timer = setTimeout(() => setHighlightedId(null), HIGHLIGHT_DURATION_MS);
    return () => clearTimeout(timer);
  }, [highlightedId]);

  const openTemplate = useCallback(
    (template: MediaManagerTemplate) => {
      navigate(Pages.MEDIA_MANAGER.builders.editor(template.id));
    },
    [navigate],
  );

  useEffect(() => {
    setHeaderContent(<UDHeaderTitle>Медиа менеджер</UDHeaderTitle>);
    return () => setHeaderContent(null);
  }, [setHeaderContent]);

  const loadTemplates = useCallback(() => {
    void refetch();
  }, [refetch]);

  const existingNames = useMemo(() => templates.map((t) => t.name.trim()), [templates]);
  const { modal: createModal, open: openCreateModal } = useCreateTemplateModal({
    tournamentId: tournamentId ?? null,
    existingNames,
    templatesCount: templates.length,
    onCreated: loadTemplates,
  });
  const { modal: editModal, open: openEditModal } = useEditTemplateModal({
    templates,
    onUpdated: loadTemplates,
  });
  const { modal: deleteModal, open: openDeleteModal } = useDeleteTemplateModal({
    onDeleted: loadTemplates,
  });

  const organizer = useSelector(organizerInfoSelector);
  const { exportTemplates, isExporting } = useExportTemplates();
  const runExport = useCallback(
    (list: readonly MediaManagerTemplate[]) => {
      exportTemplates(list, { label: organizer?.name }).catch((error) => {
        console.error('Failed to export media manager templates:', error);
        toast.error('Не удалось выгрузить шаблоны, попробуйте ещё раз');
        // The usual cause is a stale list — a template deleted in another tab is
        // «not found» on the full read. Refresh it, or the retry fails the same way.
        loadTemplates();
      });
    },
    [exportTemplates, organizer?.name, loadTemplates],
  );
  const exportOne = useCallback((template: MediaManagerTemplate) => runExport([template]), [runExport]);

  const importTarget = useMemo(() => (tournamentId ? { tournamentId } : null), [tournamentId]);
  const {
    pickFile,
    fileInput,
    isImporting,
    result: importResult,
    clearResult: clearImportResult,
    pending: pendingImport,
    confirmOverwrite,
    cancelImport,
  } = useImportTemplates({ target: importTarget, existing: templates, templatesCount: templates.length });

  // A single imported template is flashed in the list, like a fresh duplicate.
  useEffect(() => {
    if (importResult?.status !== 'done') return;
    const touched = [...importResult.created, ...importResult.overwritten];
    if (touched.length === 1) setHighlightedId(touched[0].id);
  }, [importResult]);

  return (
    <div className="flex h-full flex-col p-6">
      <div className="mb-4 flex justify-end gap-3">
        <UDButton
          variant="secondary"
          className="flex items-center"
          disabled={!importTarget || isImporting}
          loading={isImporting}
          onClick={pickFile}
        >
          <Upload size={16} className="mr-2" />
          Импорт из файла
        </UDButton>
        <UDButton
          variant="secondary"
          className="flex items-center"
          disabled={templates.length === 0 || isExporting}
          loading={isExporting}
          onClick={() => runExport(templates)}
        >
          <Download size={16} className="mr-2" />
          Экспорт всех
        </UDButton>
        <UDButton variant="secondary" className="flex items-center" onClick={() => navigate(Pages.MEDIA_MANAGER.TEMPLATES_LIBRARY)}>
          <LayoutTemplate size={16} className="mr-2" />
          Общие шаблоны
        </UDButton>
        <UDButton variant="dark" className="flex items-center" onClick={openCreateModal}>
          <Plus size={16} className="mr-2" />
          Добавить шаблон
        </UDButton>
      </div>

      <MediaManagerTemplateList
        templates={templates}
        state={listState}
        highlightedTemplateId={highlightedId}
        onCreate={openCreateModal}
        onRetry={loadTemplates}
        onSelect={openTemplate}
        onEdit={openEditModal}
        onDelete={openDeleteModal}
        onExport={exportOne}
        isExporting={isExporting}
      />

      {createModal}
      {editModal}
      {deleteModal}
      {fileInput}
      {pendingImport && <ImportConflictsModal pending={pendingImport} onConfirm={confirmOverwrite} onCancel={cancelImport} />}
      {importResult && <ImportTemplatesResultModal result={importResult} onClose={clearImportResult} />}
    </div>
  );
};

export default MediaManagerTemplatesListPage;
